import { vec2 } from "@tlaukkan/tsm";
import { Entity, Not, System } from "ecsy";
import { queryRadius } from "../box2dUtils";
import { Antibody, ConvertChargeToEffectRadius, Cytokine, Dead, EffectRadius, ElectricCharge, ElectricConductor, ElectricSink, ElementInfo, InstantDamage, PreviewOnly, Transform, UnitOfElectricCurrent, Virus } from "../components";
import { BehaviorsConstants } from "../constants";
import { getId } from "../entityFactory";
import { Game } from "../game";
import { addComponent, getOrAddComponent } from "../utils";

const SUNK_CURRENT_STICKAROUND = 0.2;

export class ElectricitySystem extends System {
	private game: Game;

	allCurrent = new Array<UnitOfElectricCurrent>();
	recentlySunkCurrent = new Array<{ position: vec2, timeSinceDissapation: number }>();

	//private activeCurrent = new Map<Entity, Set<UnitOfElectricCurrent>>();

	constructor(world: any, attributes: any) {
		// @ts-ignore
		super(world, attributes);
		this.game = attributes.game;
	}

	execute(delta: number, time: number) {
		//Handle electric range set effect first
		for (const entity of this.queries.chargeToRangeConversions.results) {
			const charge = entity.getComponent(ElectricCharge);
			const converter = entity.getComponent(ConvertChargeToEffectRadius);
			const effectRadius = entity.getComponent(EffectRadius);
			effectRadius.radius = charge.charge * converter.conversionRate;
		}

		//TODO: Need to make sure edges to ophan charges stay connected until charge passes through back to source.

		/*0. Clear all conductor nodes (TODO: UGH OPT) */
		for (const entity of this.queries.allConductors.results) {
			const conductor = entity.getComponent(ElectricConductor);
			conductor.incoming.clear();
			conductor.outgoing.clear();
			conductor.accessCharge = false;
		}
		//This recently sunk list is just so we can remember to draw current towards viruses that just died
		this.recentlySunkCurrent.forEach(sunk => sunk.timeSinceDissapation += delta);
		this.recentlySunkCurrent = this.recentlySunkCurrent.filter(sunk => sunk.timeSinceDissapation < SUNK_CURRENT_STICKAROUND);

		/*1. For everything that has charge, give it a conductor node*/
		for (const entity of this.queries.sources.results) {
			getOrAddComponent(entity, ElectricConductor);
		}

		/*2. For every conductor, check for nearby immune cells, proteins, and viruses within range and make sure they have a conductor node.
			 If they don't, create one and connect it with an edge pointing towards the creator node */
		//const conductors = [...this.queries.livingConductors.results];
		const conductors = [...this.queries.sources.results];

		let i = 0;
		while (i < conductors.length) {
			const entity = conductors[i++];
			const conductor = entity.getComponent(ElectricConductor);
			conductor.accessCharge = true;

			for (const possibleNewConductor of conductor.targets) {
				if (!this.isConnected(conductor, possibleNewConductor) && this.canConduct(possibleNewConductor)) {

					let otherConductor: ElectricConductor;
					let couldHaveTargetsThisFrame = false;
					if (!possibleNewConductor.hasComponent(ElectricConductor)) {
						otherConductor = addComponent(possibleNewConductor, ElectricConductor);
					}
					else {
						otherConductor = possibleNewConductor.getComponent(ElectricConductor);
						couldHaveTargetsThisFrame = true;
					}

					//Slow down propagation a bit, this is an imprecise optimization but made a big difference. Could be expanded on probably
					//We don't really need to know the whole graph of viruses since they are likely to die
					if (possibleNewConductor.hasComponent(Virus)) {
						couldHaveTargetsThisFrame = false;
					}

					const theyHaveCharge = possibleNewConductor.hasComponent(ElectricCharge) && possibleNewConductor.getComponent(ElectricCharge).charge > 0;
					if (!theyHaveCharge) {
						//Add edge from them to us
						conductor.incoming.add(possibleNewConductor);
						otherConductor.outgoing.add(entity);
						if (couldHaveTargetsThisFrame) conductors.push(possibleNewConductor);
						continue;
					}
					//If they have have charge..
					else {
						const bothHaveCharge = entity.hasComponent(ElectricCharge) && entity.getComponent(ElectricCharge).charge > 0;
						const weAreSink = entity.hasComponent(ElectricSink);
						const theyAreSink = possibleNewConductor.hasComponent(ElectricSink);

						if (bothHaveCharge && !weAreSink && theyAreSink) {
							//Add edge from us to them
							otherConductor.incoming.add(entity);
							conductor.outgoing.add(possibleNewConductor);
							if (couldHaveTargetsThisFrame) conductors.push(possibleNewConductor);
							continue;
						}
						//If we both are charge sources
						if (bothHaveCharge) {
							//do not connect
							continue;
						}
						//If they have charge and we don't
						else {
							//Add edge from us to them
							otherConductor.incoming.add(entity);
							conductor.outgoing.add(possibleNewConductor);
							if (couldHaveTargetsThisFrame) conductors.push(possibleNewConductor);
							continue;
						}
					}
				}
			}
		}

		//From here on is charge propagation, don't do it outside of battle
		if (!this.game.inSimulation) return;

		//Useful for testing, propogate charge only every couple seconds
		//if (time % 10 !== 0) return;

		/*3. If any any conductor nodes are viruses, do a breadth first search up the graph of conductor nodes, turning edges to face towards the virus.
			 When the search reaches a conductor that also has charge, stop. If an edge already goes that direction, increase its weight to 1. */
		/*For every immune cell that draws current, do the same search at lower priority than the virus search.  */

		for (const entity of this.queries.drawingSources.results) {
			this.drawCurrent(entity);
		}

		for (const entity of this.queries.conductingViruses.results) {
			this.drawCurrent(entity);
		}

		/*4 If any electric source has outgoing edges and no current, create current*/
		for (const entity of this.queries.sources.results) {
			const conductor = entity.getComponent(ElectricConductor);
			if (conductor.liveCurrent.size === 0
				&& entity.getComponent(ElectricCharge).charge > 0
				&& conductor.outgoing.size > 0
			) {
				this.createCurrent(entity);
			}
		}

		/*5. For every conductor node that has charge, and and outgoing edge, propogate the charge along the edge to the next conductor. 
			 Don't propogate if the destination already has current.
			 In the case it could go two directions, we go towards the smaller entity id in order to stay deterministic. 
			 We could go towards the closer one here but trying to skip the distance query for now. See how it works
			 If the conductor is dead or if there is nowhere to conduct (no edges, including full ones) dissapate the current.
		*/
		const currentToDissapate = new Array<UnitOfElectricCurrent>();
		for (const current of this.allCurrent) {
			const entity = current.pathTraveled[0];
			const conductor = entity.getComponent(ElectricConductor);
			if (!conductor) { //conductor died
				currentToDissapate.push(current);
				continue;
			}
			if (conductor.outgoing.size === 0) {
				//If we have no where to go, current dissipates
				currentToDissapate.push(current);
				continue;
			};

			const destination = Array.from(conductor.outgoing).sort((a, b) => (getId(a) || 0) - (getId(b) || 0))[0];
			this.transferCurrent(current, destination);
		}

		for (const current of currentToDissapate) {
			this.dissapateCurrent(current);
		}

		/*6. For every virus, if it has received charge and has no outgoing edge, take damage*/
		//Implemented this part in damage system...
		for (const entity of this.queries.conductingViruses.results) {
			const conductor = entity.getComponent(ElectricConductor);
			if (conductor.liveCurrent.size > 0) {
				const deterministicCoinFlip = getStepNumber() % 2 === (getId(entity) || 0) % 2;
				const shouldDie = conductor.outgoing.size === 0 || deterministicCoinFlip;
				if (shouldDie) {
					//Sink or dissapate all current objects (in case of multiple sources)
					let damageDone = false;
					const currentHere = Array.from(conductor.liveCurrent);
					for (let i = 0; i < currentHere.length; i++) {
						const current = currentHere[i];
						if (damageDone) {
							this.dissapateCurrent(current);
							continue;
						}
						const source = current.source.getComponent(ElectricCharge);
						if (source && source.charge > 0) {
							//At this point we can sink the current from the source
							addComponent(entity, InstantDamage, { damage: 1 })
							//And mark this as spent charge
							this.sinkCurrent(current);
							damageDone = true;
						}
					}
				}
			}
		}

		/*7. For every immune cell that has received charge and has ElectricSink, store the charge*/
		for (const entity of this.queries.drawingSources.results) {
			const conductor = entity.getComponent(ElectricConductor);
			const charge = entity.getComponent(ElectricCharge);
			if (conductor.liveCurrent.size > 0 && conductor.outgoing.size === 0) {
				const currents = Array.from(conductor.liveCurrent);
				for (const current of currents) {
					const gained = this.sinkCurrent(current);
					charge.charge += gained;
				}
			}
		}
	}

	drawCurrent(entity: Entity) {
		const isSourceItself = entity.hasComponent(ElectricCharge); //some entities both have and draw charge
		const visited = new Set<Entity>();
		let currentDepth = [entity];
		let nextDepth = new Array<Entity>();
		let reachedCharge = false;
		while (!reachedCharge && currentDepth.length > 0) {
			for (const current of currentDepth) {
				if (visited.has(current)) continue;
				visited.add(current);

				const currentConductor = current.getComponent(ElectricConductor);
				const foundOurOwnCharge = isSourceItself && current === entity;

				if (currentConductor.liveCurrent.size > 0 && !foundOurOwnCharge) reachedCharge = true;
				let edges = Array.from(currentConductor.outgoing);
				if (isSourceItself) edges = edges.concat(Array.from(currentConductor.incoming)); //if we are a drawing source edges might point away from us when they otherwise wouldn't
				edges = edges.sort((a, b) => (getId(a) || 0) - (getId(b) || 0)); //Sort by id to stay deterministic

				for (const other of edges) {
					const otherConductor = other.getComponent(ElectricConductor);
					if (!otherConductor) {
						console.warn('Found entity in electricity network with no other conductor');
						continue;
					};
					if (!visited.has(other)) {
						nextDepth.push(other);
						//Flip edge towards virus
						currentConductor.outgoing.delete(other);
						currentConductor.incoming.add(other);
						otherConductor.incoming.delete(current);
						otherConductor.outgoing.add(current);
					}
				}
			}
			currentDepth = nextDepth;
			nextDepth = [];
		}
	}

	canConduct(entity: Entity) {
		return entity.hasComponent(Antibody) || entity.hasComponent(Virus) || (entity.hasComponent(Cytokine));
	}

	isConnected(conductor: ElectricConductor, entity: Entity) {
		return conductor.incoming.has(entity) || conductor.outgoing.has(entity);

	}

	sinkCurrent(current: UnitOfElectricCurrent) {
		const entity = current.pathTraveled[0];
		const conductor = entity.getComponent(ElectricConductor);
		if (conductor.liveCurrent.size > 0) {
			const source = current.source.getComponent(ElectricCharge);
			if (source.charge > 0) { //defensively prevent draining below 0, hopefully no longer possible
				entity.getMutableComponent(ElectricConductor); //flag changed
				const source = current.source.getMutableComponent(ElectricCharge);
				source.charge -= 1;
				return 1;
			}

			this.dissapateCurrent(current);
			const transform = entity.getComponent(Transform);
			if (transform) {
				this.recentlySunkCurrent.push({ position: transform.position, timeSinceDissapation: 0 });
			}
		}
		return 0;
	}

	createCurrent(source: Entity) {
		const newcurrent = { source, pathTraveled: [source] };
		this.allCurrent.push(newcurrent);
		getOrAddComponent(source, ElectricConductor).liveCurrent.add(newcurrent);
	}

	dissapateCurrent(current: UnitOfElectricCurrent) {
		const entity = current.pathTraveled[0];
		const conductor = entity.getComponent(ElectricConductor);
		if (conductor) { //could be dead entity
			conductor.liveCurrent.delete(current);
		}
		this.allCurrent.splice(this.allCurrent.indexOf(current), 1);
	}

	transferCurrent(current: UnitOfElectricCurrent, destination: Entity) {
		const source = current.pathTraveled[0];
		const fromConductor = source.getComponent(ElectricConductor);
		fromConductor.liveCurrent.delete(current);

		const toConductor = destination.getComponent(ElectricConductor);
		toConductor.liveCurrent.add(current);
		current.pathTraveled.unshift(destination);
	}
}

ElectricitySystem.queries = {
	sources: { components: [ElectricCharge] },
	allConductors: { components: [ElectricConductor] },
	livingConductors: { components: [ElectricConductor, Transform, Not(Dead)] },
	conductingViruses: { components: [ElectricConductor, Virus, Not(Dead)] },
	drawingSources: { components: [ElectricCharge, ElectricSink, Not(Dead)] },
	chargeToRangeConversions: { components: [ConvertChargeToEffectRadius, EffectRadius, ElectricCharge, Not(Dead)] },
};